/**
 * Financials Reconciliation Utility
 * 
 * Purpose:
 *   Provides reconciliation checks to verify that period_summary totals match
 *   the sum of reservation-level financial data. Used for financial correctness
 *   validation in debug mode only.
 * 
 * Workflow:
 *   Accepts period_summary and reservations_minimal -> sums reservation-level
 *   fields -> compares to period_summary totals -> returns structured report.
 * 
 * Assumptions / Invariants:
 *   - Non-blocking: never throws, never changes values, never affects production UI
 *   - Debug-only: intended for ?debug_financials=1 or DEV mode
 *   - Tolerance: 0.01 (cents rounding) for floating-point comparisons
 *   - Missing fields: returns not_applicable if reservation-level fields don't exist
 *   - No tax aggregation: does NOT sum transient_occupancy_tax with lodging_tax+sales_tax
 *     (backend handles tax aggregation; frontend is display-only)
 * 
 * Dependencies:
 *   - None (pure function, no external dependencies)
 * 
 * Version: 1.0.0
 * 
 * Changelog:
 *   - 1.0.0 (Phase 3): Initial reconciliation utility for FinancialsPage debug mode.
 */

/**
 * Reconcile period_summary totals against reservation-level sums
 * @param {Object} params - Parameters
 * @param {Object} params.period_summary - Period summary object with totals
 * @param {Array} params.reservations_minimal - Array of reservation objects with canonical_financial_summary
 * @returns {Object} Reconciliation report with status and checks
 */
export function reconcileFinancials({ period_summary, reservations_minimal = [] }) {
  const report = {
    status: 'pass', // 'pass' | 'fail' | 'not_applicable'
    checks: [],
    summary: {
      period_summary_totals: {},
      reservation_sums: {},
      differences: {},
    },
    timestamp: new Date().toISOString(),
  };

  const tolerance = 0.01; // Cents rounding tolerance

  // Helper to safely extract numeric value
  const getNumeric = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = typeof value === 'number' ? value : parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  // Helper to check if field exists in reservations
  const hasField = (reservations, path) => {
    if (!reservations || reservations.length === 0) return false;
    // Check first reservation (sample check)
    const first = reservations[0];
    if (!first || !first.canonical_financial_summary) return false;
    
    const parts = path.split('.');
    let obj = first.canonical_financial_summary;
    for (const part of parts) {
      if (obj === null || obj === undefined || typeof obj !== 'object') return false;
      if (!(part in obj)) return false;
      obj = obj[part];
    }
    return obj !== null && obj !== undefined;
  };

  // Check 1: guest_total_paid
  const guestTotalPaidCheck = {
    field: 'guest_total_paid',
    period_total: getNumeric(period_summary?.guest_total_paid),
    reservation_sum: 0,
    difference: 0,
    status: 'not_applicable',
  };

  if (hasField(reservations_minimal, 'guest_paid.guest_total_paid')) {
    guestTotalPaidCheck.reservation_sum = reservations_minimal.reduce((sum, r) => {
      const value = getNumeric(r.canonical_financial_summary?.guest_paid?.guest_total_paid);
      return sum + value;
    }, 0);
    guestTotalPaidCheck.difference = Math.abs(guestTotalPaidCheck.period_total - guestTotalPaidCheck.reservation_sum);
    guestTotalPaidCheck.status = guestTotalPaidCheck.difference <= tolerance ? 'pass' : 'fail';
  }

  report.checks.push(guestTotalPaidCheck);
  report.summary.period_summary_totals.guest_total_paid = guestTotalPaidCheck.period_total;
  report.summary.reservation_sums.guest_total_paid = guestTotalPaidCheck.reservation_sum;
  report.summary.differences.guest_total_paid = guestTotalPaidCheck.difference;

  // Check 2: owner_payout_total
  const ownerPayoutCheck = {
    field: 'owner_payout_total',
    period_total: getNumeric(period_summary?.owner_payout_total),
    reservation_sum: 0,
    difference: 0,
    status: 'not_applicable',
  };

  if (hasField(reservations_minimal, 'owner_payout.owner_payout_total')) {
    ownerPayoutCheck.reservation_sum = reservations_minimal.reduce((sum, r) => {
      const value = getNumeric(r.canonical_financial_summary?.owner_payout?.owner_payout_total);
      return sum + value;
    }, 0);
    ownerPayoutCheck.difference = Math.abs(ownerPayoutCheck.period_total - ownerPayoutCheck.reservation_sum);
    ownerPayoutCheck.status = ownerPayoutCheck.difference <= tolerance ? 'pass' : 'fail';
  }

  report.checks.push(ownerPayoutCheck);
  report.summary.period_summary_totals.owner_payout_total = ownerPayoutCheck.period_total;
  report.summary.reservation_sums.owner_payout_total = ownerPayoutCheck.reservation_sum;
  report.summary.differences.owner_payout_total = ownerPayoutCheck.difference;

  // Check 3: owner_channel_fees (use absolute value for comparison)
  const channelFeesCheck = {
    field: 'owner_channel_fees',
    period_total: Math.abs(getNumeric(period_summary?.owner_channel_fees)),
    reservation_sum: 0,
    difference: 0,
    status: 'not_applicable',
  };

  if (hasField(reservations_minimal, 'owner_payout.owner_channel_fees')) {
    channelFeesCheck.reservation_sum = reservations_minimal.reduce((sum, r) => {
      const value = getNumeric(r.canonical_financial_summary?.owner_payout?.owner_channel_fees);
      return sum + Math.abs(value); // Channel fees are negative, use abs
    }, 0);
    channelFeesCheck.difference = Math.abs(channelFeesCheck.period_total - channelFeesCheck.reservation_sum);
    channelFeesCheck.status = channelFeesCheck.difference <= tolerance ? 'pass' : 'fail';
  }

  report.checks.push(channelFeesCheck);
  report.summary.period_summary_totals.owner_channel_fees = channelFeesCheck.period_total;
  report.summary.reservation_sums.owner_channel_fees = channelFeesCheck.reservation_sum;
  report.summary.differences.owner_channel_fees = channelFeesCheck.difference;

  // Check 4: guest_tourist_tax
  const touristTaxCheck = {
    field: 'guest_tourist_tax',
    period_total: getNumeric(period_summary?.guest_tourist_tax),
    reservation_sum: 0,
    difference: 0,
    status: 'not_applicable',
  };

  if (hasField(reservations_minimal, 'guest_paid.guest_tourist_tax')) {
    touristTaxCheck.reservation_sum = reservations_minimal.reduce((sum, r) => {
      const value = getNumeric(r.canonical_financial_summary?.guest_paid?.guest_tourist_tax);
      return sum + value;
    }, 0);
    touristTaxCheck.difference = Math.abs(touristTaxCheck.period_total - touristTaxCheck.reservation_sum);
    touristTaxCheck.status = touristTaxCheck.difference <= tolerance ? 'pass' : 'fail';
  }

  report.checks.push(touristTaxCheck);
  report.summary.period_summary_totals.guest_tourist_tax = touristTaxCheck.period_total;
  report.summary.reservation_sums.guest_tourist_tax = touristTaxCheck.reservation_sum;
  report.summary.differences.guest_tourist_tax = touristTaxCheck.difference;

  // Determine overall status
  const hasFailures = report.checks.some(check => check.status === 'fail');
  const hasApplicableChecks = report.checks.some(check => check.status !== 'not_applicable');

  if (hasFailures) {
    report.status = 'fail';
  } else if (!hasApplicableChecks) {
    report.status = 'not_applicable';
  } else {
    report.status = 'pass';
  }

  return report;
}
